import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import { useCreateRequest } from '@/domain/requests'
import { useMultipleRecipientsStore } from '@/store/useMultipleRecipientsStore'
import { useSendStore } from '@/store/useSendStore'
import { useUserStore } from '@/store/useUserStore'
import { currencyUtils } from '@/utils'

const { formatFiatCurrency, parseAmountToFloat } = currencyUtils

interface UseHandleMultipleRequestsProps {
  type: 'send' | 'request'
  currencyValue: string
  currencyCode: string
  fiatAmount: string
  fiatCode: string
}

interface RequestResult {
  recipientId: string
  recipientName: string
  success: boolean
  error?: string
}

export const useHandleMultipleRequests = ({
  type,
  currencyValue,
  currencyCode,
  fiatAmount,
  fiatCode,
}: UseHandleMultipleRequestsProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)

  const selectedRecipients = useMultipleRecipientsStore(
    (s) => s.selectedRecipients,
  )
  const note = useSendStore((s) => s.note)
  const resetSend = useSendStore((s) => s.resetSend)
  const myId = useUserStore((s) => s.userAuthenticated?.user_id)

  const createRequestMutation = useCreateRequest()

  const handleSendMultipleRequests = useCallback(async () => {
    if (type !== 'request') {
      toast.error(t('errors.onlyRequestsSupported', {
        defaultValue: 'Only requests are supported for multiple recipients',
      }))
      return false
    }

    if (selectedRecipients.length === 0) {
      toast.error(t('errors.noRecipientsSelected', {
        defaultValue: 'No recipients selected',
      }))
      return false
    }

    setIsLoading(true)
    const results: RequestResult[] = []

    try {
      // Send requests sequentially
      for (const recipient of selectedRecipients) {
        try {
          await createRequestMutation.mutateAsync({
            initiator_user_id: myId || '',
            acceptor_user_id: recipient.id,
            acceptor_phone_number: recipient.phoneNumber,
            request_type: 'send_request',
            extra_info: {
              amount: currencyValue,
              currency: currencyCode,
              note: note || '',
            },
          })

          results.push({
            recipientId: recipient.id,
            recipientName: recipient.name,
            success: true,
          })
        } catch (error) {
          console.error(`Failed to send request to ${recipient.name}:`, error)
          results.push({
            recipientId: recipient.id,
            recipientName: recipient.name,
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
          })
        }
      }

      const successCount = results.filter((r) => r.success).length
      const failureCount = results.filter((r) => !r.success).length

      // Reset send store before navigation
      resetSend()

      // Navigate to success screen with results
      const relativeFiatFormatted = formatFiatCurrency({
        fiatCode,
        value: parseAmountToFloat({
          amount: fiatAmount || '0',
          decimalSeparator: '.',
        }),
        showDecimals: true,
      })

      navigate('/send-request/success-multiple', {
        state: {
          results,
          successCount,
          failureCount,
          amount: currencyValue,
          code: currencyCode,
          relativeFiatFormatted,
          type: 'request',
        },
      })

      return true
    } catch (error) {
      console.error('Error in handleSendMultipleRequests:', error)
      toast.error(t('errors.somethingWrong'))
      return false
    } finally {
      setIsLoading(false)
    }
  }, [
    type,
    selectedRecipients,
    myId,
    currencyValue,
    currencyCode,
    note,
    createRequestMutation,
    t,
    resetSend,
    navigate,
    fiatAmount,
    fiatCode,
  ])

  return {
    handleSendMultipleRequests,
    isLoading,
  }
}

